import { html } from 'onefold';
import { CodeBlock } from '../../components/CodeBlock';
import { Playground } from '../../components/Playground';
import { Callout } from '../../components/Callout';

export function QuickStartPage(): Node {
  return html`
    <div>
      <h1>Quick Start</h1>
      <p>Build your first onefold app in under 2 minutes.</p>

      <h2>1. Create a Project</h2>
      ${CodeBlock(`npm create onefold@latest my-app
cd my-app
npm install`)}

      <h2>2. Write a Component</h2>
      <p>Open <code>src/main.ts</code> and replace its content:</p>
      ${CodeBlock(`import { createSignal, html, mount } from 'onefold';

function App(): Node {
  const name = createSignal('World');

  return html\`
    <div>
      <h1>Hello, \${() => name()}!</h1>
      <input
        type="text"
        value=\${() => name()}
        oninput=\${(e: Event) => name.set((e.target as HTMLInputElement).value)}
      />
    </div>
  \`;
}

mount(App(), document.getElementById('app')!);`)}

      <h2>3. Run It</h2>
      ${CodeBlock(`npm run dev
# → http://localhost:3000`)}

      <p>Type in the input — the heading updates instantly. That's reactive signals at work.</p>

      <h2>Try It Live</h2>
      ${Playground(`import { createSignal, html, mount } from 'onefold';

function App(): Node {
  const name = createSignal('World');

  return html\`
    <div>
      <h1>Hello, \${() => name()}!</h1>
      <input
        type="text"
        value=\${() => name()}
        oninput=\${(e) => name.set(e.target.value)}
      />
    </div>
  \`;
}

mount(App(), document.getElementById('app'));`, 'Hello World')}

      <h2>Key Concepts</h2>
      <ul>
        <li><strong><code>createSignal(value)</code></strong> — creates a reactive value. Call it to read, call <code>.set()</code> to write.</li>
        <li><strong><code>html\`...\`</code></strong> — a tagged template that builds real DOM nodes. Wrap dynamic values in <code>() =></code> to make them reactive.</li>
        <li><strong><code>mount(node, el)</code></strong> — attaches a component tree to the page.</li>
      </ul>

      ${Callout('The most common mistake: forgetting the () => arrow wrapper. html`<p>${count}</p>` renders once and never updates. html`<p>${() => count()}</p>` updates every time count changes.')}

      <h2>4. Build for Production</h2>
      ${CodeBlock(`npm run build     # → dist/
npm run preview   # → http://localhost:4000`)}

      <p>The production build uses esbuild — typically completes in under 50ms.</p>

      <h2>Next Steps</h2>
      <ul>
        <li>Learn about <a href="/core/signals">Signals</a> — the reactivity primitive</li>
        <li>Explore <a href="/core/templates">Templates</a> — how to write UI</li>
        <li>Add <a href="/routing/router">Routing</a> — for multi-page apps</li>
      </ul>
    </div>
  `;
}
